import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { Autocomplete, createFilterOptions } from "@material-ui/lab";
import axios from "axios";
import { URL } from "../../config/config";
import { SnackbarContext } from "../../commons/Snackbar/Snackbar";

// First dropdown option; picking it removes the current assignee
const NONE_OPTION = { _id: null, name: "N/A" };
const REMOVE_STYLE = { backgroundColor: "#d32f2f", color: "#ffffff" };
const filterStaff = createFilterOptions();

// Staff list is the same for every client, so fetch it once per page load
let staffRequest = null;
const loadStaff = () => {
  if (!staffRequest) {
    staffRequest = axios
      .get(`${URL}/superadmin/get-agents`)
      .then(({ data }) => data || [])
      .catch((error) => {
        staffRequest = null; // let the next client view retry
        throw error;
      });
  }
  return staffRequest;
};

// PRO-262: one block per role from GET /client-account-team
const ClientAccountTeam = ({ clientId, canWrite, containerClass }) => {
  const { showSnackbar } = useContext(SnackbarContext);
  // showSnackbar changes on every snackbar render; a ref keeps fetchRoles stable
  const snackbarRef = useRef(showSnackbar);
  snackbarRef.current = showSnackbar;
  const [roles, setRoles] = useState([]);
  const [staff, setStaff] = useState([]);
  // idle | loading | loaded | error; staff loads on first dropdown open
  const [staffStatus, setStaffStatus] = useState("idle");
  const [selected, setSelected] = useState({});
  const [savingRole, setSavingRole] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  // Skip state updates if the page closes while staff is still loading
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const fetchRoles = useCallback(async () => {
    try {
      const { data } = await axios.get(`${URL}/superadmin/client-account-team`, {
        params: { clientId },
      });
      setRoles(data?.roles || []);
    } catch (error) {
      setRoles([]);
      snackbarRef.current(error.response?.data?.message || "Unable to load account team", "error");
    }
  }, [clientId]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const openStaffPicker = () => {
    if (staffStatus === "loading" || staffStatus === "loaded") return;
    setStaffStatus("loading");
    loadStaff()
      .then((list) => {
        if (!mountedRef.current) return;
        setStaff(list);
        setStaffStatus("loaded");
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setStaff([]);
        setStaffStatus("error");
      });
  };

  const setAssignee = async (roleKey, staffAgentId) => {
    setSavingRole(roleKey);
    try {
      const { data } = await axios.post(`${URL}/superadmin/set-client-assignee`, {
        clientId,
        role: roleKey,
        staffAgentId,
      });
      const messages = {
        assigned: "Assigned successfully",
        removed: "Removed successfully",
        unchanged: "No change",
      };
      showSnackbar(messages[data?.result] || "Saved", "success");
      setSelected((prev) => ({ ...prev, [roleKey]: undefined }));
      await fetchRoles();
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Unable to save", "error");
    } finally {
      setSavingRole(null);
    }
  };

  // A CSM gets WhatsApp alerts, so staff without a phone can't be picked
  const isOptionDisabled = (roleKey) => (option) =>
    roleKey === "csm" && option?._id !== null && !option?.isCurrent && !option?.contact;

  // What the dropdown shows: the admin's pick, else the current assignee, else N/A
  const pickedOption = (role) => {
    if (selected[role.key] !== undefined) return selected[role.key];
    if (!role.currentAgentId) return NONE_OPTION;
    // Before the list loads, show the current assignee from the role itself
    return (
      staff.find((option) => option._id === role.currentAgentId) || {
        _id: role.currentAgentId,
        name: role.current,
        isCurrent: true,
      }
    );
  };

  // Options always include the shown value, so MUI doesn't warn about an unknown value
  const optionsFor = (role) => {
    const picked = pickedOption(role);
    const inList = !picked || picked._id === null || staff.some((o) => o._id === picked._id);
    return inList ? [NONE_OPTION, ...staff] : [NONE_OPTION, picked, ...staff];
  };

  // Button text and state follow what the save would actually do
  const actionFor = (role) => {
    const picked = pickedOption(role);
    const pickedId = picked ? picked._id : undefined;
    const currentId = role.currentAgentId || null;
    if (pickedId === undefined || pickedId === currentId) {
      return { label: currentId ? `Change ${role.label}` : `Assign ${role.label}`, disabled: true };
    }
    if (pickedId === null) return { label: `Remove ${role.label}`, remove: true };
    return { label: currentId ? `Change ${role.label}` : `Assign ${role.label}`, pickedId };
  };

  return (
    <Box py={2} px={2} my={2} className={containerClass}>
      {roles.map((role, roleIndex) => (
        <Box key={role.key} mt={roleIndex ? 6 : 0}>
          <Box>
            <Typography variant="body1" gutterBottom style={{ fontWeight: "bold" }}>
              Assign {role.label}
            </Typography>

            <Typography variant="body1" gutterBottom>
              Current {role.label} - {role.current || "N/A"}
            </Typography>

            {canWrite && (
              <Grid container>
                <Grid item xs={5}>
                  <Autocomplete
                    value={pickedOption(role)}
                    options={optionsFor(role)}
                    // Until the list loads, show "Loading..." or the error instead of options
                    filterOptions={(opts, state) =>
                      staffStatus === "loaded" ? filterStaff(opts, state) : []
                    }
                    onOpen={openStaffPicker}
                    loading={staffStatus === "loading"}
                    loadingText="Loading..."
                    getOptionSelected={(option, value) => option._id === value._id}
                    getOptionLabel={(option) => option?.name || ""}
                    getOptionDisabled={isOptionDisabled(role.key)}
                    renderOption={(option) =>
                      isOptionDisabled(role.key)(option)
                        ? `${option.name} (No phone number)`
                        : option.name
                    }
                    onChange={(e, value) =>
                      setSelected((prev) => ({ ...prev, [role.key]: value }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        style={{ border: "1px solid ", background: "white", marginRight: 10 }}
                        placeholder={`Select ${role.label}`}
                      />
                    )}
                    noOptionsText={
                      staffStatus === "error"
                        ? "Unable to load staff, close and open again"
                        : "No staff found"
                    }
                  />
                </Grid>

                <Grid item>
                  {(() => {
                    const action = actionFor(role);
                    return (
                      <Button
                        style={{ marginLeft: 10, ...(action.remove && !action.disabled ? REMOVE_STYLE : {}) }}
                        color="primary"
                        variant="contained"
                        disabled={action.disabled || savingRole === role.key}
                        onClick={() =>
                          action.remove
                            ? setConfirmRemove(role)
                            : setAssignee(role.key, action.pickedId)
                        }
                      >
                        {action.label}
                      </Button>
                    );
                  })()}
                </Grid>
              </Grid>
            )}
          </Box>

          <Box mt={4}>
            <Typography variant="body1" gutterBottom style={{ fontWeight: "bold" }}>
              {role.label} Assign History
            </Typography>

            <TableContainer component={Paper} style={{ width: "100%", maxWidth: 800 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: "25%" }}>From</TableCell>
                    <TableCell style={{ width: "25%" }}>To</TableCell>
                    <TableCell style={{ width: "30%" }}>Updated By</TableCell>
                    <TableCell style={{ width: "30%" }}>Updated On</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(role.history || []).map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.from || "N/A"}</TableCell>
                      <TableCell>{row.to || "N/A"}</TableCell>
                      <TableCell>{row.updatedBy}</TableCell>
                      <TableCell>
                        {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : ""}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      ))}

      <Dialog open={!!confirmRemove} onClose={() => setConfirmRemove(null)}>
        <DialogTitle>Remove {confirmRemove?.label}?</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {confirmRemove?.current} will no longer be the {confirmRemove?.label} for this client.
            {confirmRemove?.key === "csm" &&
              " The client will stop seeing their Account Manager and can't request callbacks."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemove(null)}>Cancel</Button>
          <Button
            style={REMOVE_STYLE}
            variant="contained"
            onClick={() => {
              const role = confirmRemove;
              setConfirmRemove(null);
              setAssignee(role.key, null);
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientAccountTeam;
